// src/pages/admin/ManageListings.tsx
import { useEffect, useState } from 'react';
import { Trash2, Eye, Car, Search, Edit2, Save, X } from 'lucide-react';
import { Listing } from '../../lib/supabase';
import {
  adminGetApprovedListings,
  adminUpdateListing,
  adminDeleteListing,
} from '../../lib/adminApi';
import { AdminLayout } from '../../components/AdminLayout';

export function ManageListings() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  
  // Edit Mode States
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Listing>>({});

  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterBrand, setFilterBrand] = useState('');

  useEffect(() => {
    loadApprovedListings();
  }, []);

  const loadApprovedListings = async () => {
    try {
      const data = await adminGetApprovedListings();
      setListings(data);
    } catch (error) {
      console.error('Error loading approved listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette annonce? Cette action est irréversible.')) return;

    setActionLoading(true);
    try {
      await adminDeleteListing(id);

      setListings(listings.filter(listing => listing.id !== id));
      setSelectedListing(null);
      alert('Annonce supprimée avec succès!');
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Une erreur est survenue lors de la suppression de l\'annonce');
    } finally {
      setActionLoading(false);
    }
  };

  // Open modal in View or Edit mode
  const openModal = (listing: Listing, editMode: boolean = false) => {
    setSelectedListing(listing);
    setEditFormData(listing); // Initialize form data
    setIsEditing(editMode);
  };

  // Handle Update Listing
  const handleUpdate = async () => {
    if (!selectedListing || !editFormData) return;

    setActionLoading(true);
    try {
      await adminUpdateListing(selectedListing.id, {
        brand: editFormData.brand,
        model: editFormData.model,
        price: editFormData.price,
        year: editFormData.year,
        mileage: editFormData.mileage,
        fuel_type: editFormData.fuel_type,
        transmission: editFormData.transmission,
        location: editFormData.location,
        condition: editFormData.condition,
        description: editFormData.description,
        contact_phone: editFormData.contact_phone,
        contact_email: editFormData.contact_email,
      });

      // Update local state
      const updatedListing = { ...selectedListing, ...editFormData } as Listing;
      setListings(listings.map(l => l.id === selectedListing.id ? updatedListing : l));
      setSelectedListing(updatedListing);
      setIsEditing(false);
      alert('Annonce mise à jour avec succès!');
    } catch (error) {
      console.error('Error updating listing:', error);
      alert(`Erreur lors de la mise à jour : ${(error as any)?.message || 'Erreur inconnue'}${(error as any)?.code ? ` (code ${(error as any).code})` : ''}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: name === 'price' || name === 'year' || name === 'mileage' ? Number(value) : value
    }));
  };

  // Filter listings
  const filteredListings = listings.filter(listing => {
    const matchesSearch = searchTerm === '' || 
      listing.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.model.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLocation = filterLocation === '' || listing.location === filterLocation;
    const matchesBrand = filterBrand === '' || listing.brand === filterBrand;
    
    return matchesSearch && matchesLocation && matchesBrand;
  });

  const uniqueLocations = [...new Set(listings.map(listing => listing.location))];
  const uniqueBrands = [...new Set(listings.map(listing => listing.brand))];

  return (
    <AdminLayout currentPage="listings">
      {/* Header and Filters (Unchanged) */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Gestion des Annonces</h2>
        <p className="text-sm text-gray-600">
          {loading ? 'Chargement...' : `${listings.length} annonce${listings.length !== 1 ? 's' : ''} approuvée${listings.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Search and Filters Block */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Rechercher par marque ou modèle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
            >
              <option value="">Toutes les localisations</option>
              {uniqueLocations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
            >
              <option value="">Toutes les marques</option>
              {uniqueBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterLocation('');
                setFilterBrand('');
              }}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Loading & Lists */}
      {loading ? (
        <div className="space-y-3">
            {/* Skeleton Loaders */}
           {[1, 2, 3].map((i) => (
             <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
                <div className="w-24 h-20 bg-gray-200 rounded"></div>
             </div>
           ))}
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center">
          <Car className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg text-gray-600">Aucune annonce trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredListings.map((listing) => (
            <div key={listing.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-start space-x-3">
                <div className="w-24 h-20 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                  {listing.images && listing.images.length > 0 ? (
                    <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400"><Car /></div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{listing.brand} {listing.model}</h3>
                      <p className="text-xl font-bold text-[#D4452C] mt-1">{listing.price.toLocaleString()} TND</p>
                    </div>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">Approuvée</span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600 mb-3">
                    <div><span className="font-medium">Année:</span> {listing.year}</div>
                    <div><span className="font-medium">Km:</span> {listing.mileage.toLocaleString()}</div>
                    <div><span className="font-medium">Carburant:</span> {listing.fuel_type}</div>
                    <div><span className="font-medium">Boîte:</span> {listing.transmission}</div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openModal(listing, false)}
                      className="flex items-center space-x-1 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    >
                      <Eye className="h-3 w-3" />
                      <span>Détails</span>
                    </button>
                    <button
                      onClick={() => openModal(listing, true)}
                      className="flex items-center space-x-1 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-sm text-blue-600"
                    >
                      <Edit2 className="h-3 w-3" />
                      <span>Modifier</span>
                    </button>
                    <button
                      onClick={() => handleDelete(listing.id)}
                      disabled={actionLoading}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal - Handles both View and Edit */}
      {selectedListing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedListing(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900">
                    {isEditing ? 'Modifier l\'annonce' : 'Détails de l\'annonce'}
                  </h3>
                  {!isEditing && (
                    <button 
                      onClick={() => setIsEditing(true)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Modifier"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setSelectedListing(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-6">
              {/* Images (Read-only for now) */}
              {selectedListing.images && selectedListing.images.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Photos</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedListing.images.map((image, index) => (
                      <img key={index} src={image} alt={`Photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                    ))}
                  </div>
                </div>
              )}

              {/* Form / Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Brand */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Marque</label>
                  {isEditing ? (
                    <input name="brand" value={editFormData.brand} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                  ) : <p className="font-medium">{selectedListing.brand}</p>}
                </div>
                
                {/* Model */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Modèle</label>
                  {isEditing ? (
                    <input name="model" value={editFormData.model} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                  ) : <p className="font-medium">{selectedListing.model}</p>}
                </div>

                {/* Price */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Prix (TND)</label>
                  {isEditing ? (
                    <input type="number" name="price" value={editFormData.price} onChange={handleInputChange} className="w-full border p-2 rounded text-sm font-bold text-[#D4452C]" />
                  ) : <p className="font-bold text-[#D4452C]">{selectedListing.price.toLocaleString()} TND</p>}
                </div>

                {/* Year */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Année</label>
                  {isEditing ? (
                    <input type="number" name="year" value={editFormData.year} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                  ) : <p className="font-medium">{selectedListing.year}</p>}
                </div>

                {/* Mileage */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Kilométrage</label>
                  {isEditing ? (
                    <input type="number" name="mileage" value={editFormData.mileage} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                  ) : <p className="font-medium">{selectedListing.mileage.toLocaleString()} km</p>}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Localisation</label>
                  {isEditing ? (
                    <input name="location" value={editFormData.location} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                  ) : <p className="font-medium">{selectedListing.location}</p>}
                </div>
                
                {/* Fuel */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Carburant</label>
                  {isEditing ? (
                     <select name="fuel_type" value={editFormData.fuel_type} onChange={handleInputChange} className="w-full border p-2 rounded text-sm">
                        <option value="Essence">Essence</option>
                        <option value="Diesel">Diesel</option>
                        <option value="Hybride">Hybride</option>
                        <option value="Electrique">Electrique</option>
                     </select>
                  ) : <p className="font-medium">{selectedListing.fuel_type}</p>}
                </div>

                 {/* Transmission */}
                 <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Boîte</label>
                  {isEditing ? (
                     <select name="transmission" value={editFormData.transmission} onChange={handleInputChange} className="w-full border p-2 rounded text-sm">
                        <option value="Manuelle">Manuelle</option>
                        <option value="Automatique">Automatique</option>
                     </select>
                  ) : <p className="font-medium">{selectedListing.transmission}</p>}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                {isEditing ? (
                  <textarea name="description" rows={4} value={editFormData.description} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                ) : <p className="text-gray-600 whitespace-pre-wrap text-sm">{selectedListing.description}</p>}
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 p-3 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Téléphone</label>
                    {isEditing ? (
                        <input name="contact_phone" value={editFormData.contact_phone} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                    ) : <p className="font-medium">{selectedListing.contact_phone}</p>}
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                    {isEditing ? (
                        <input name="contact_email" value={editFormData.contact_email} onChange={handleInputChange} className="w-full border p-2 rounded text-sm" />
                    ) : <p className="font-medium">{selectedListing.contact_email}</p>}
                </div>
              </div>

              {/* Action Buttons inside Modal */}
              <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
                {isEditing ? (
                    <>
                        <button
                            onClick={() => setIsEditing(false)}
                            disabled={actionLoading}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleUpdate}
                            disabled={actionLoading}
                            className="flex items-center space-x-2 px-4 py-2 bg-[#D4452C] text-white rounded-md hover:bg-[#b03a23] text-sm"
                        >
                            {actionLoading ? <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent"></div> : <Save className="h-4 w-4" />}
                            <span>Enregistrer</span>
                        </button>
                    </>
                ) : (
                    <button
                        onClick={() => handleDelete(selectedListing.id)}
                        className="flex items-center space-x-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                    >
                        <Trash2 className="h-3 w-3" />
                        <span>Supprimer l'annonce</span>
                    </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}